import React, {useState} from "react";
import {ServerResponse} from "../../../models/models";
import {RESPONSE_TYPES, UPDATE_SUB_ACTION} from "../../../constants";
import {
    convertPriceUnitAmountToReadable, daysBetween,
    getDailyRateOfPlan, getFormattedMessage,
    loadResponsePage
} from "../../../helpers/helpers";
import GenericItem from "../../common/items/GenericItem";
import BackButton from "../../common/buttons/BackButton";
import GenericButton from "../../common/buttons/GenericButton";
import SyncLoader from "react-spinners/SyncLoader";
import PlanSubscriptionChangeConfirm from "../../common/subscriptions/PlanSubscriptionChangeConfirm";
import PlanRecurrenceChangeConfirm from "../../common/subscriptions/PlanRecurrenceChangeConfirm";

const getUpdateAction = (selectedPlan) => {
    if (selectedPlan.downgrade) {
        return UPDATE_SUB_ACTION.DOWNGRADE;
    } else if (selectedPlan.upgrade) {
        return UPDATE_SUB_ACTION.UPGRADE;
    }
    return UPDATE_SUB_ACTION.RECURRENCE;
}

const UpdateSubscriptionConfirmPage = (props: {
    selectedPlan: Object,
    handleError: Function,
    setServerResponse: Function,
    setPage: Function,
    setSelectedPlan: Function,
    getSameTierPlanAtDifferentRecurrence: Function,
    pages: Object,
    updateSubscription: Function
}) => {
    const {
        data, selectedPlan, handleError, setServerResponse, setPage,
        getSameTierPlanAtDifferentRecurrence, pages, updateSubscription
    } = props;

    if (selectedPlan === null || !data) {
        return <></>;
    }

    const {doNotRenew, levels, expirationDate, hasActiveFreeTrial, freeTrialExpirationDate} = data;

    const [showMoreUpgradeInfo, setShowMoreUpgradeInfo] = useState(false);
    const [loading, setLoading] = useState(false);

    const getPlanSelectLabel = (label, monthly) => {
        return `${label} ${monthly ? `(${i18n.payments.monthly})` : `(${i18n.payments.annual})`}`;
    };

    const newlySelectedPlanLabel = getPlanSelectLabel(selectedPlan.label, selectedPlan.months === 1);
    const daysUntilExpired = daysBetween(new Date(), new Date(expirationDate));

    let Body = <></>;
    let pageTitleText;
    let detailsEl;
    let submitBtnText;
    if (selectedPlan.downgrade) {
        const previousPlanLabel = getPlanSelectLabel(selectedPlan.oldLabel, selectedPlan.months === 1);
        pageTitleText = i18n.payments.downgradingSubscription;
        submitBtnText = i18n.payments.confirmDowngrade;

        const priceText = convertPriceUnitAmountToReadable(selectedPlan.priceAmount);
        const extendText = selectedPlan.months === 1 ? i18n.payments.oneMonth : i18n.payments.oneYear;

        detailsEl = (
            <div>
                {getFormattedMessage(i18n.payments.sub.downgradeDetails, daysUntilExpired + 1, priceText, extendText)}
            </div>
        );

        Body = (
            <PlanSubscriptionChangeConfirm selectedPlan={selectedPlan}
                                           previousPlanLabel={previousPlanLabel}
                                           doNotRenew={doNotRenew}
                                           newlySelectedPlanLabel={newlySelectedPlanLabel}/>
        );
    } else if (selectedPlan.upgrade) {
        const previousPlanLabel = getPlanSelectLabel(selectedPlan.oldLabel, selectedPlan.oldMonths === 1);
        pageTitleText = i18n.payments.upgradingSubscription;
        submitBtnText = i18n.payments.confirmUpgrade;

        const detailsTextList = [];

        const dailyRateOfOldPlan = getDailyRateOfPlan(selectedPlan.oldPriceAmount, selectedPlan.oldMonths === 1);
        const dailyRateOfNewPlan = getDailyRateOfPlan(selectedPlan.priceAmount, selectedPlan.months === 1);
        detailsTextList.push(<li hidden={!showMoreUpgradeInfo}
                                 key={'details-1'}>{getFormattedMessage(i18n.payments.sub.dailyRateOfCurrentSub, dailyRateOfOldPlan)}</li>);
        detailsTextList.push(<li hidden={!showMoreUpgradeInfo}
                                 key={'details-2'}>{getFormattedMessage(i18n.payments.sub.dailyRateOfNewSub, dailyRateOfNewPlan)}</li>);

        const amountSpendTowardsOldPlan = (dailyRateOfOldPlan * daysUntilExpired).toFixed(2);
        const amountNeededForNewPlan = (dailyRateOfNewPlan * daysUntilExpired).toFixed(2);
        detailsTextList.push(<li hidden={!showMoreUpgradeInfo}
                                 key={'details-3'}>{getFormattedMessage(i18n.payments.sub.amountSpendTowardsCurrentSub, amountSpendTowardsOldPlan, daysUntilExpired + 1)}</li>);
        detailsTextList.push(<li hidden={!showMoreUpgradeInfo}
                                 key={'details-4'}>{getFormattedMessage(i18n.payments.sub.amountNeededForNewSub, amountNeededForNewPlan, daysUntilExpired + 1)}</li>);

        const proratedCostDue = (amountNeededForNewPlan - amountSpendTowardsOldPlan).toFixed(2);
        detailsTextList.push(<li hidden={!showMoreUpgradeInfo} key={'details-5'}>${amountNeededForNewPlan} -
            ${amountSpendTowardsOldPlan} = ${proratedCostDue}</li>)

        const priceText = convertPriceUnitAmountToReadable(selectedPlan.priceAmount);
        const extendText = selectedPlan.months === 1 ? i18n.payments.oneMonth : i18n.payments.oneYear;

        let upgradeDetails;
        if (hasActiveFreeTrial && freeTrialExpirationDate) {
            const freeTrialDate = new Date(freeTrialExpirationDate);
            const daysUntilFreeTrialExpires = daysBetween(new Date(), freeTrialDate) + 1;
            upgradeDetails = getFormattedMessage(i18n.payments.sub.upgradeFreeTrialDetails,
                daysUntilFreeTrialExpires, priceText, extendText);
        } else {
            upgradeDetails = getFormattedMessage(i18n.payments.sub.upgradeDetails,
                proratedCostDue, priceText, extendText);
        }

        detailsTextList.push(
            <li key={'details-6'}>
                {upgradeDetails}
                {
                    !hasActiveFreeTrial && (
                        <span className={'show-more-info-toggle'}
                              onClick={() => setShowMoreUpgradeInfo(val => !val)}>
                            {showMoreUpgradeInfo ? `(${i18n.payments.hideInfo})` : `(${i18n.payments.showInfo})`}
                        </span>
                    )
                }
            </li>
        );

        detailsEl = (
            <ul>
                {detailsTextList.map((listEl) => listEl)}
            </ul>
        );

        Body = (
            <PlanSubscriptionChangeConfirm selectedPlan={selectedPlan}
                                           previousPlanLabel={previousPlanLabel}
                                           doNotRenew={doNotRenew}
                                           newlySelectedPlanLabel={newlySelectedPlanLabel}/>
        );
    } else if (selectedPlan.changeRecurrence) {
        pageTitleText = i18n.payments.updatingRecurrence;
        submitBtnText = i18n.payments.confirmChange;
        const previousPlan = getSameTierPlanAtDifferentRecurrence(levels, selectedPlan);
        const previousPlanLabel = getPlanSelectLabel(previousPlan.label, previousPlan.months === 1);

        const priceText = `$${convertPriceUnitAmountToReadable(selectedPlan.priceAmount)}`;
        const extendText = selectedPlan.months === 1 ? i18n.payments.oneMonth : i18n.payments.oneYear;
        detailsEl = (
            <div>
                {getFormattedMessage(i18n.payments.sub.changeRecurrence, daysUntilExpired + 1, priceText, extendText)}
            </div>
        );

        Body = (
            <PlanRecurrenceChangeConfirm selectedPlan={selectedPlan}
                                         previousPlan={previousPlan}
                                         previousPlanLabel={previousPlanLabel}
                                         doNotRenew={doNotRenew}
                                         newlySelectedPlanLabel={newlySelectedPlanLabel}/>
        );
    }

    const onBackClick = () => {
        setPage(pages.UPDATE_SUBSCRIPTION);
    }

    const onSubmitClick = () => {
        setLoading(true);
        updateSubscription(getUpdateAction(selectedPlan), selectedPlan.value, (result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to update subscription: ', result);
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            } else if (result.data.error !== null && result.data.error !== undefined) {
                setServerResponse(new ServerResponse(RESPONSE_TYPES.error, i18n.payments.error.genericTitle,
                    result.data.error));
                return;
            }

            loadResponsePage('succeeded', setLoading, setServerResponse);
        });
    };

    return (
        <div className={"row"}>
            <div className={"col-12"}>
                <GenericItem title={pageTitleText}
                             details={''}/>

                {Body}

                <GenericItem title={i18n.payments.areYouSure}
                             details={detailsEl}/>

                <div className={"row"}>
                    <div className={"col-12"}>
                        <div className={"flexSpaceBetween footerMargins"}>
                            <BackButton onClick={onBackClick}
                                        hidden={loading}/>
                            <GenericButton onClick={onSubmitClick}
                                           hidden={loading}
                                           text={submitBtnText}/>
                            {loading && (<div><SyncLoader/></div>)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateSubscriptionConfirmPage;